import React, { useRef, useState } from "react";
import { Badge, Box, Group, Table, Text, UnstyledButton } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconChevronRight } from "@tabler/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { PageList, ENUM_FORM_DISPLAY } from "../../components/pageList";
import { useCellRender } from "../../hooks/useCellRender";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useServerApi } from "../../hooks/userServerApi";
import { openFileUrl } from "../../utils/openReport";
import { DATA_ORDER_STATUS } from "../../data/options";
import EntityTabAnchor from "./payroll2/EntityTabAnchor";

const apiEntity = "commissionStatementRow";

const formSchema = {
  title: "Commission Statement Overview",
  apiEntity,
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "lg" },
  initialValues: {},
  layout: { fields: [] },
};

const rowTypeLabel = (type, t) => {
  const normalized = (type || "INITIAL").toUpperCase();
  if (normalized === "RENEWAL") {
    return t("commissionStatement.rowType.renewal", "Renewal");
  }
  if (normalized === "TRAILING") {
    return t("commissionStatement.rowType.trailing", "Trailing");
  }
  return t("commissionStatement.rowType.initial", "Initial");
};

const DN_POLICY_STATUS_LABEL = {
  NONE: "None",
  POLICY_RECEIVED: "Policy Received",
  POLICY_DISPATCHED: "Policy Dispatched",
};

const policyStatusColor = (status) => {
  if (status === "POLICY_RECEIVED" || status === "INF") return "green";
  if (status === "POLICY_DISPATCHED") return "blue";
  if (!status || status === "NONE") return "gray";
  if (["CAN", "TER", "REJ", "WTH", "LAP", "SUR"].includes(status)) return "red";
  if (status === "DFT" || status === "PND") return "yellow";
  return "blue";
};

const policyStatusLabel = (data, t) => {
  if (!data.policyStatus) return "--";
  if (data.policyStatusSource === "DEBIT_NOTE") {
    return t(DN_POLICY_STATUS_LABEL[data.policyStatus] || data.policyStatus);
  }
  const match = DATA_ORDER_STATUS.find((item) => item.value === data.policyStatus);
  return t(match?.label || data.policyStatus);
};

const roleLabel = (role, t) => {
  if (role === "PRODUCER") return t("Producer");
  if (role === "OVERRIDE") return t("Override");
  if (role === "COMPANY") return t("Company");
  return role || "--";
};

const formatPayoutDate = (value, cellRender) =>
  value ? cellRender.Date(value, "YYYY-MM-DD") : "--";

const PayrollPayouts = ({ payrolls, payouts, t, cellRender }) => {
  if (!payrolls?.length) {
    return (
      <PayoutDetails payouts={payouts} t={t} cellRender={cellRender} />
    );
  }

  return payrolls.map((payroll) => {
    const period =
      payroll.fromPeriod || payroll.toPeriod
        ? `${formatPayoutDate(payroll.fromPeriod, cellRender)} – ${formatPayoutDate(payroll.toPeriod, cellRender)}`
        : "";
    return (
      <Box
        key={payroll._id || payroll.code}
        sx={{ "& + &": { borderTop: "1px solid #e9ecef" } }}
      >
        <Group
          position="apart"
          align="center"
          noWrap
          sx={{
            padding: "8px 12px",
            background: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <EntityTabAnchor
              entity="payroll"
              id={payroll._id}
              label={payroll.code}
              size="xs"
              weight={600}
            />
            <Text size="xs" color="dimmed">
              {`${t("Created")} ${formatPayoutDate(payroll.createdAt, cellRender)} · ${t("Confirmed")} ${formatPayoutDate(payroll.confirmedAt, cellRender)}`}
              {period ? ` · ${t("Period")} ${period}` : ""}
            </Text>
          </Box>
          {payroll.status ? cellRender.Status(payroll.status) : "--"}
        </Group>
        <PayoutDetails
          payouts={payroll.payouts}
          t={t}
          cellRender={cellRender}
        />
      </Box>
    );
  });
};

const PayoutDetails = ({ payouts, t, cellRender }) => {
  if (!payouts?.length) {
    return (
      <Text size="xs" color="dimmed" sx={{ display: "block", padding: "10px 12px" }}>
        {t("No payroll payout yet")}
      </Text>
    );
  }

  return (
    <Table
      fontSize="xs"
      verticalSpacing={6}
      horizontalSpacing="sm"
      sx={{
        display: "table",
        width: "100%",
        tableLayout: "fixed",
        "& thead th": {
          background: "#f8f9fa",
          color: "#495057",
          fontWeight: 600,
          borderBottom: "1px solid #e9ecef",
          whiteSpace: "nowrap",
        },
        "& tbody td": {
          background: "#fff",
          borderBottom: "1px solid #f1f3f5",
          verticalAlign: "middle",
        },
        "& tbody tr:last-of-type td": {
          borderBottom: "none",
        },
      }}
    >
      <thead>
        <tr>
          <th style={{ width: "24%" }}>{t("Party")}</th>
          <th style={{ width: "14%" }}>{t("Staff ID")}</th>
          <th style={{ width: "16%" }}>{t("Role")}</th>
          <th style={{ width: "10%" }}>{t("Level")}</th>
          <th style={{ width: "18%", textAlign: "right" }}>{t("Pay Out")}</th>
          <th style={{ width: "18%" }}>{t("Status")}</th>
        </tr>
      </thead>
      <tbody>
        {payouts.map((payout) => (
          <tr key={payout._id || `${payout.tree}-${payout.level}-${payout.userName}`}>
            <td>{payout.userName || "--"}</td>
            <td>{payout.staffId || "--"}</td>
            <td>{roleLabel(payout.role, t)}</td>
            <td>{payout.level ?? "--"}</td>
            <td style={{ textAlign: "right" }}>
              {cellRender.Currency(payout.paidAmount, "HKD")}
            </td>
            <td>{payout.status ? cellRender.Status(payout.status) : "--"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const CommissionStatementOverview = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const [cellRender] = useCellRender();
  const [api] = useServerApi();
  const queryRef = useRef({ condition: {}, sort: { by: "createdAt", order: "desc" } });
  const [exporting, setExporting] = useState(false);
  const [openIds, setOpenIds] = useState({});
  const statementListPath = location.pathname.startsWith("/management/")
    ? "/management/commissionStatement"
    : "/account/commissionStatement";

  const toggleRow = (id) => {
    setOpenIds((current) => ({ ...current, [id]: !current[id] }));
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const data = await api.CommissionStatement.exportOverview({
        condition: queryRef.current.condition || {},
        sort: queryRef.current.sort,
        roleLabels: {
          PRODUCER: t("Producer"),
          OVERRIDE: t("Override"),
          COMPANY: t("Company"),
        },
        columns: [
          { key: "code", title: t("Statement Number") },
          { key: "period", title: t("Statement Period") },
          { key: "insurerName", title: t("Insurer") },
          { key: "policy", title: t("Policy Number") },
          { key: "policyStatus", title: t("Policy Status") },
          { key: "applReceivedDate", title: t("Appl. Recv.") },
          { key: "policyReceivedDate", title: t("Policy Recv.") },
          { key: "orderNumber", title: t("Order Number") },
          { key: "debitNoteCode", title: t("Debit Note Number") },
          { key: "clientName", title: t("Client") },
          { key: "productName", title: t("Product") },
          { key: "type", title: t("Type") },
          { key: "premiumReceived", title: t("Premium Received") },
          { key: "payIn", title: t("Pay In") },
          { key: "status", title: t("Status") },
          { key: "party", title: t("Party") },
          { key: "staffId", title: t("Staff ID") },
          { key: "role", title: t("Role") },
          { key: "level", title: t("Level") },
          { key: "partyPayOut", title: t("Pay Out") },
          { key: "payoutStatus", title: t("Payout Status") },
        ],
      });
      if (!data?.url) throw new Error("Export failed");
      await openFileUrl(data.url);
    } catch (error) {
      showNotification({
        title: t("Export Excel"),
        message: error?.message || t("Export Excel"),
        color: "red",
      });
    } finally {
      setExporting(false);
    }
  };

  const tableSchema = {
    showActionColumn: false,
    horizontalScroll: true,
    searchableFields: [
      "code",
      "policy",
      "insuredName",
      "clientName",
      "orderCode",
      "debitNoteCode",
    ],
    renderSubRow: (data, colSpan) => {
      if (!openIds[data._id]) return null;
      return (
        <tr>
          <td
            colSpan={colSpan}
            style={{
              background: "#f8f9fa",
              padding: 0,
              whiteSpace: "normal",
              verticalAlign: "top",
            }}
          >
            <Box
              sx={{
                position: "sticky",
                left: 0,
                display: "flex",
                flexDirection: "column",
                width: "var(--table-viewport-width, 100cqi)",
                maxWidth: "var(--table-viewport-width, 100cqi)",
                boxSizing: "border-box",
                padding: "10px 12px 14px",
              }}
            >
              <Box
                sx={{
                  background: "#fff",
                  border: "1px solid #e9ecef",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <Text
                  size="xs"
                  weight={600}
                  sx={{
                    display: "block",
                    padding: "8px 12px",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  {t("Payout Details")}
                </Text>
                <PayrollPayouts
                  payrolls={data.payrolls}
                  payouts={data.payouts}
                  t={t}
                  cellRender={cellRender}
                />
              </Box>
            </Box>
          </td>
        </tr>
      );
    },
    columns: [
      {
        field: "expand",
        headerName: "",
        minWidth: 36,
        cellRender: (col, data) => (
          <UnstyledButton onClick={() => data._id && toggleRow(data._id)}>
            {openIds[data._id] ? (
              <IconChevronDown size={14} />
            ) : (
              <IconChevronRight size={14} />
            )}
          </UnstyledButton>
        ),
      },
      {
        field: "code",
        headerName: "Statement Number",
        sortable: true,
        minWidth: 150,
        cellRender: (col, data) => (
          <Text
            size="xs"
            color="blue"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              if (!data.statementId) return;
              tabNav.openDetail({
                pathname: statementListPath,
                id: data.statementId,
                label: data.code || t("Commission Statement"),
              });
            }}
          >
            {data.code || "--"}
          </Text>
        ),
      },
      {
        field: "period",
        headerName: "Statement Period",
        sortable: true,
        minWidth: 120,
        cellRender: (col, data) => data.period || "--",
      },
      {
        field: "insurerName",
        headerName: "Insurer",
        sortable: true,
        minWidth: 220,
        cellRender: (col, data) => data.insurerName || "--",
      },
      {
        field: "policy",
        headerName: "Policy Number",
        sortable: true,
        minWidth: 140,
        cellRender: (col, data) => data.policy || "--",
      },
      {
        field: "policyStatus",
        headerName: "Policy Status",
        sortable: true,
        minWidth: 160,
        cellRender: (col, data) =>
          data.policyStatus ? (
            <Badge
              size="xs"
              radius={0}
              color={policyStatusColor(data.policyStatus)}
            >
              {policyStatusLabel(data, t)}
            </Badge>
          ) : (
            "--"
          ),
      },
      {
        field: "applReceivedDate",
        headerName: "Appl. Recv.",
        sortable: true,
        minWidth: 120,
        cellRender: (col, data) =>
          data.applReceivedDate
            ? cellRender.Date(data.applReceivedDate, "YYYY-MM-DD")
            : "--",
      },
      {
        field: "policyReceivedDate",
        headerName: "Policy Recv.",
        sortable: true,
        minWidth: 120,
        cellRender: (col, data) =>
          data.policyReceivedDate
            ? cellRender.Date(data.policyReceivedDate, "YYYY-MM-DD")
            : "--",
      },
      {
        field: "orderNumber",
        headerName: "Order Number",
        sortable: true,
        minWidth: 140,
        cellRender: (col, data) => data.orderNumber || data.orderCode || "--",
      },
      {
        field: "debitNoteCode",
        headerName: "Debit Note Number",
        sortable: true,
        minWidth: 150,
        cellRender: (col, data) => data.debitNoteCode || "--",
      },
      {
        field: "clientName",
        headerName: "Client",
        sortable: true,
        minWidth: 160,
        cellRender: (col, data) => data.clientName || "--",
      },
      {
        field: "productName",
        headerName: "Product",
        sortable: true,
        minWidth: 180,
        cellRender: (col, data) => data.productName || "--",
      },
      {
        field: "type",
        headerName: "Type",
        sortable: true,
        minWidth: 100,
        cellRender: (col, data) => rowTypeLabel(data.type, t),
      },
      {
        field: "producerName",
        headerName: "Producer",
        minWidth: 160,
        cellRender: (col, data) =>
          data.producerName
            ? `${data.producerName}${data.producerStaffId ? ` (${data.producerStaffId})` : ""}`
            : "--",
      },
      {
        field: "premiumReceived",
        headerName: "Premium Received",
        sortable: true,
        minWidth: 130,
        cellRender: (col, data) => (
          <Box sx={{ textAlign: "right" }}>
            {cellRender.Currency(data.premiumReceived, data.premiumCurrency)}
          </Box>
        ),
      },
      {
        field: "totalRcv",
        headerName: "Pay In",
        sortable: true,
        minWidth: 120,
        cellRender: (col, data) => (
          <Box sx={{ textAlign: "right" }}>
            {cellRender.Currency(data.totalRcv, "HKD")}
          </Box>
        ),
      },
      {
        field: "payOut",
        headerName: "Pay Out",
        minWidth: 120,
        cellRender: (col, data) => (
          <Box sx={{ textAlign: "right" }}>
            {cellRender.Currency(data.payOut, "HKD")}
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 100,
        cellRender: (col, data) => cellRender.Status(data.status),
      },
    ],
    advanceFilters: [
      {
        field: "createdAt",
        type: "dateRange",
        label: t("Statement Date"),
      },
      {
        field: "period",
        type: "text",
        label: t("Statement Period"),
        placeholder: "YYYYMM",
      },
      {
        field: "code",
        type: "text",
        label: t("Statement Number"),
      },
      {
        field: "provider",
        type: "RemoteSelect",
        label: t("Insurer"),
        props: {
          apiEntity: "provider",
          valueField: "_id",
          select: "name code shortName cname",
          searchFields: ["name", "code", "shortName", "cname"],
          labelField: "name",
          labelRender: (data) =>
            _.truncate(`[ ${data.code} ] ${data.name} ${data.cname ?? ""}`, {
              length: 65,
            }),
          sort: {
            by: "code",
            order: "asc",
          },
          pageSize: 10000,
        },
      },
      {
        field: "status",
        type: "select",
        label: t("Status"),
        options: [
          { label: t("Pending"), value: "PENDING" },
          { label: t("Verified"), value: "VERIFIED" },
          { label: t("Confirmed"), value: "CONFIRMED" },
        ],
      },
      {
        field: "type",
        type: "select",
        label: t("Type"),
        options: [
          { label: t("commissionStatement.rowType.initial", "Initial"), value: "INITIAL" },
          { label: t("commissionStatement.rowType.renewal", "Renewal"), value: "RENEWAL" },
          { label: t("commissionStatement.rowType.trailing", "Trailing"), value: "TRAILING" },
        ],
      },
      {
        field: "policy",
        type: "text",
        label: t("Policy Number"),
      },
      {
        field: "clientName",
        type: "text",
        label: t("Client"),
      },
    ],
  };

  return (
    <PageList
      title="Commission Statement Overview"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      readOnly
      initSort={{ by: "createdAt", order: "desc" }}
      onQueryChange={(condition, sort) => {
        queryRef.current = { condition, sort };
      }}
      customActions={[
        {
          label: t("Export Excel"),
          variant: "default",
          loading: exporting,
          onClick: exportExcel,
        },
      ]}
    />
  );
};

export default CommissionStatementOverview;
